<template>
  <div class="region-selector" ref="dropdownRef">
    <!-- Botón principal -->
    <button 
      @click="toggleDropdown"
      class="flex items-center space-x-1.5 px-3 py-2 rounded-md text-indigo-600"
      :aria-label="currentRegion?.name || 'Select region'"
      aria-haspopup="true"
      :aria-expanded="isOpen"
    >
      <!-- Bandera y nombre de región actual -->
      <span class="text-lg leading-none">{{ regionFlag }}</span>
      <span class="text-sm font-medium">{{ regionShortName }}</span>
    </button>

    <!-- Dropdown menu -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div 
        v-show="isOpen"
        class="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden"
      >
        <div class="py-1" role="menu" aria-orientation="vertical">
          <!-- Opción España -->
          <button
            @click="changeRegion('es')"
            class="group flex items-center justify-between w-full px-4 py-2.5 text-sm transition-colors duration-150"
            :class="regionId === 'es' 
              ? 'bg-indigo-50 text-indigo-700 font-medium' 
              : 'text-gray-700 hover:bg-gray-50'"
            role="menuitem"
          >
            <div class="flex items-center space-x-3">
              <span class="text-xl">🇪🇸</span>
              <div class="text-left">
                <div class="font-medium">España</div>
                <div class="text-xs text-gray-500">Madrid, Barcelona, Valencia...</div>
              </div>
            </div>
            <svg 
              v-if="regionId === 'es'"
              class="w-5 h-5 text-indigo-600" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </button>

          <!-- Opción United States (New York) -->
          <button
            @click="changeRegion('us')"
            class="group flex items-center justify-between w-full px-4 py-2.5 text-sm transition-colors duration-150"
            :class="regionId === 'us' 
              ? 'bg-indigo-50 text-indigo-700 font-medium' 
              : 'text-gray-700 hover:bg-gray-50'"
            role="menuitem"
          >
            <div class="flex items-center space-x-3">
              <span class="text-xl">🇺🇸</span>
              <div class="text-left">
                <div class="font-medium">United States</div>
                <div class="text-xs text-gray-500">New York</div>
              </div>
            </div>
            <svg 
              v-if="regionId === 'us'"
              class="w-5 h-5 text-indigo-600" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </button>

          <!-- Opción United Kingdom (London) -->
          <button
            @click="changeRegion('uk')"
            class="group flex items-center justify-between w-full px-4 py-2.5 text-sm transition-colors duration-150"
            :class="regionId === 'uk' 
              ? 'bg-indigo-50 text-indigo-700 font-medium' 
              : 'text-gray-700 hover:bg-gray-50'"
            role="menuitem"
          >
            <div class="flex items-center space-x-3">
              <span class="text-xl">🇬🇧</span>
              <div class="text-left">
                <div class="font-medium">United Kingdom</div>
                <div class="text-xs text-gray-500">London</div>
              </div>
            </div>
            <svg 
              v-if="regionId === 'uk'"
              class="w-5 h-5 text-indigo-600" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </button>

          <!-- Separador -->
          <div class="my-1 border-t border-gray-200"></div>

          <!-- Selector de Idioma -->
          <div class="px-3 py-2">
            <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {{ currentLocale === 'es' ? 'Idioma' : 'Language' }}
            </p>
            <div class="flex gap-2">
              <!-- Español -->
              <button
                @click="changeLanguage('es')"
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-150"
                :class="currentLocale === 'es' 
                  ? 'bg-indigo-50 text-indigo-700 font-medium ring-1 ring-indigo-200' 
                  : 'text-gray-600 hover:bg-gray-50'"
              >
                <span class="text-base">🇪🇸</span>
                <span>ES</span>
              </button>
              
              <!-- English -->
              <button
                @click="changeLanguage('en')"
                class="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-150"
                :class="currentLocale === 'en' 
                  ? 'bg-indigo-50 text-indigo-700 font-medium ring-1 ring-indigo-200' 
                  : 'text-gray-600 hover:bg-gray-50'"
              >
                <span class="text-base">🇬🇧</span>
                <span>EN</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRegion } from '@/composables/useRegion'
import { useAnalytics } from '@/composables/useAnalytics'
import Trans from '@/i18n/translation'

// Composables
const { 
  regionId, 
  regionFlag, 
  regionShortName, 
  currentRegion, 
  setRegion,
  initializeRegion
} = useRegion()

const { track } = useAnalytics()

// Idioma actual
const currentLocale = computed(() => Trans.currentLocale)

// Estado del dropdown
const isOpen = ref(false)
const dropdownRef = ref(null)

// Inicializar región al montar
onMounted(() => {
  initializeRegion()
  document.addEventListener('mousedown', closeDropdown)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', closeDropdown)
})

// Toggle dropdown
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

// Cambiar región
const changeRegion = (newRegionId) => {
  if (newRegionId === regionId.value) {
    isOpen.value = false
    return
  }
  
  // Track region change
  track('region_change', {
    from: regionId.value,
    to: newRegionId,
    source: 'navbar'
  })
  
  setRegion(newRegionId)
  isOpen.value = false

  // Opcional: Recargar la página para actualizar contenido según región
  // window.location.reload()
}

// Cerrar dropdown al hacer click fuera
const closeDropdown = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

// Cambiar idioma
const changeLanguage = async (locale) => {
  if (locale === currentLocale.value) return
  
  await Trans.switchLanguage(locale)
  
  // Track language change
  track('language_change', {
    from: currentLocale.value,
    to: locale,
    source: 'region_selector_navbar'
  })
}
</script>

<style scoped>
.region-selector {
  position: relative;
  display: inline-block;
}

/* Asegurar que el dropdown esté por encima de otros elementos */
.region-selector > div {
  position: absolute;
}
</style>
